import {Module, VuexModule, Mutation, Action} from "vuex-module-decorators";
import {defaultFilters} from "./helpers";
import {TableField, PageData} from "../../types";
import {rawApi, api} from "@/lib";
import * as modals from "@/lib/modals";
import router from "../../../router";
import Store from "../../index";
import {formatBool, formatDate, formatZip, getFileName} from "../../../utils";
import {
  SplitTruckSummary,
  SplitTruckSummaryOrder,
  SplitTruckRequest,
  SplitTruckResponse
} from "@/models/api/trucks/splitTrucks";
import {
  TruckListItem,
  TruckListFilters,
  GetTrucksRequest,
  UpdateSplitTruckSummaryField,
  Truck
} from "@/models/api/trucks/getTrucks";
import {CreateTruckRequest, CreateTruckResponse} from "@/models/api/trucks/createTrucks";
import {UpdateTruckResponse, UpdateTruckRequest} from "@/models/api/trucks/updateTrucks";
import {TruckEventResource} from "@/models/api/trucks/events";
import {DeleteTruckRequest, DeleteTruckResponse} from "@/models/api/trucks/deleteTrucks";
import {
  FinalizeTruckRequest,
  FinalizeTruckResponse,
  UnfinalizeTruckRequest,
  UnfinalizeTruckResponse
} from "@/models/api/trucks/finalizeTrucks";
import {
  AddOrderToTruckRequest,
  PreviewAddOrderToTruckRequest,
  PreviewAddOrderToTruckResource
} from "@/models/api/trucks/addOrder";
import {
  UpdateTruckDetailSequenceRequest,
  DetailSequence
} from "@/models/api/trucks/updateDetailSequence";
import {
  CalculateTimeframesRequest,
  CalculateTimeframesResponse
} from "@/models/api/trucks/timeframes";
import {
  GetDriversRequest,
  DriverListItemResource,
  DriverResource
} from "@/models/api/drivers/get";
import {
  CreateDriverRequest,
  CreateDriverResponse,
  UpdateDriverRequest,
  UpdateDriverResponse
} from "@/models/api/drivers/create";
import {CarrierResource} from "@/models/api/carriers/get";
import {RemoveOrderFromTruckRequest} from "@/models/api/trucks/removeOrder";
import {
  CombineTruckSummaryResource,
  CombineTrucksRequest,
  CombineTrucksResponse
} from "@/models/api/trucks/combineTrucks";
import {
  AddNonCdsOrderToTruckRequest,
  AddNonCdsOrderToTruckResponse
} from "@/models/api/trucks/addNonCdsOrder";
import {
  GetTruckOrderFreightDetailsRequest,
  TruckOrderFreightDetailResource
} from "@/models/api/trucks/orderFreight";
import {
  GenerateImportTruckPreviewRequest,
  GenerateImportTruckPreviewResponse,
  ImportTruckFormat,
  SaveTruckImportResponse
} from "@/models/api/trucks/importTrucks";
import {TruckCommodityResource} from "@/models/api/trucks/truckCommodities";
import {AxiosResponse} from "axios";
import {DriverManifestRequest} from "@/models/api/trucks/driverManifest";
import {BNavItem} from "bootstrap-vue";
import {GetCarrierRequest} from "../../../models/api/carriers/get";
import notifications from "../../../lib/notifications";
import {State} from "@/models/api/system/states";
import _ from "lodash";

const name = "truck";

if (Store.state[name]) {
  Store.unregisterModule(name);
}

@Module({name: name, dynamic: true, store: Store, namespaced: true})
export default class TruckModule extends VuexModule {
  public currentTruck?: Truck = null;
  public data?: Array<TruckListItem> = [];
  public filters: TruckListFilters = defaultFilters();
  public loading: boolean = false;
  public error?: boolean = null;
  public hideManifestedTruck: boolean = false;
  public showOnlyScheduledTrucks: boolean = false;
  public showOnlyIncorrectTruck: boolean = false;
  public errorMessage: string = null;
  public splitTruckSummary: SplitTruckSummary = null;
  public truckFields: Array<TableField> = [
    {key: "selected", label: "", hidden: false},
    {key: "name", sortable: true, label: "Truck Name", hidden: false},
    {
      key: "deliveryDate",
      sortable: true,
      label: "Del Date",
      formatter: formatDate,
      hidden: false
    },
    {key: "driverName", sortable: true, label: "Driver Name", hidden: false},
    {key: "driverPhoneNumber", sortable: true, label: "Driver Phone #", hidden: false},
    {key: "weight", sortable: true, label: "Weight", hidden: false, textAlign: "right"},
    {key: "totalUnits", sortable: true, label: "Total Units", hidden: false, textAlign: "right"},
    {key: "maxCubes", sortable: true, label: "Max Cubes", hidden: false, textAlign: "right"},
    {key: "cubesUsed", sortable: true, label: "Cubes Used", hidden: false, textAlign: "right"},
    {
      key: "availableCubes",
      sortable: true,
      label: "Avail Cubes",
      hidden: false,
      textAlign: "right"
    },
    {key: "stops", sortable: true, label: "Stops/Orders", hidden: false, textAlign: "right"},
    {
      key: "zip3sServiced",
      sortable: true,
      label: "Zip3s Serviced",
      formatter: formatZip,
      hidden: false
    },
    {key: "facilityCode", sortable: true, label: "Facility", hidden: false},
    {key: "carrier", sortable: true, label: "Carrier", hidden: false},
    {
      key: "updatedDate",
      sortable: true,
      label: "Last Updated",
      formatter: formatDate,
      hidden: false
    },
    {key: "updatedByFullName", sortable: true, label: "Last Updated By", hidden: false},
    {key: "status", sortable: true, label: "Status", hidden: false},
    {key: "isFinalized", sortable: true, label: "Finalized", formatter: formatBool, hidden: false}
  ];
  public pageData: PageData = {
    totalPages: null,
    totalItems: null,
    currentPage: 1,
    pageSize: 30
  };

  @Action
  async getTrucks(params: GetTrucksRequest): Promise<void> {
    const pageSize = params ? params.pageSize : null || this.pageData.pageSize || 30;
    const pageNumber = params ? params.pageNumber : null || this.pageData.currentPage + 1;

    if (pageNumber === 1) {
      this.resetTruckList();
    }

    let target = `/trucks?page=${pageNumber}&PageSize=${pageSize}`;

    if (this.filters) {
      if (this.filters.zips) {
        this.filters.zips.forEach(element => {
          target += `&zip3s=${element}`;
        });
      }

      if (this.filters.deliveryDate) {
        const dateData = this.filters.deliveryDate.split(" ~ ");
        if (!dateData[1] || dateData[1] === "null") {
          dateData[1] = dateData[0];
        }
        target += `&deliveryDateStart=${dateData[0]}&deliveryDateEnd=${dateData[1]}`;
      }

      if (this.filters.facilities) {
        this.filters.facilities.forEach(element => {
          target += `&facilities=${element}`;
        });
      }
    }

    try {
      var response = await rawApi.get<TruckListItem[]>(target);
      this.setTruckList(response);
      this.setCurrentPage(pageNumber);
      this.pageData.totalItems = response.headers.totalitems;
      this.pageData.totalPages = response.headers.totalpages;
    } catch (ex) {
      throw ex;
    } finally {
      this.setLoading(false);
    }
  }

  @Action({commit: "setSplitTruckSummary"})
  async getSplitTruckSummary(truckId: number): Promise<SplitTruckSummary> {
    const response = await rawApi.get<SplitTruckSummary>(`/trucks/${truckId}/split`);
    return response.data;
  }

  @Action
  async exportTrucks() {
    let filterData = ""

    if (this.filters) {
      if (this.filters.zips) {
        this.filters.zips.forEach(element => {
          filterData += `&zip3s=${element}`;
        });
      }

      if (this.filters.deliveryDate) {
        const dateData = this.filters.deliveryDate.split(" ~ ");
        if (!dateData[1] || dateData[1] === "null") {
          dateData[1] = dateData[0];
        }
        filterData += `&deliveryDateStart=${dateData[0]}&deliveryDateEnd=${dateData[1]}`;
      }

      if (this.filters.facilities) {
        this.filters.facilities.forEach(element => {
          filterData += `&facilities=${element}`;
        });
      }
    }

    this.context.commit("setLoading", true);
    try {

      const response = await rawApi.get<any>(`/trucks/exportTrucks?${filterData}`, {
        responseType: "blob"
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `trucks.csv`);
      document.body.appendChild(link);
      link.click();

      this.context.commit("setLoading", false);
    } catch (ex) {
      this.context.commit("setLoading", false);
      throw ex;
    }
  }

  @Action
  async calculateTimeframes(request: CalculateTimeframesRequest): Promise<CalculateTimeframesResponse> {
    const response = await rawApi.post<CalculateTimeframesResponse>(
      `/trucks/${request.truckId}/timeframes`,
      request
    );

    return response.data;
  }

  @Action
  async commitTimeframes(
    request: CalculateTimeframesResponse
  ): Promise<CalculateTimeframesResponse> {
    const response = await rawApi.put<CalculateTimeframesResponse>(
      `/trucks/${request.truckId}/timeframes`,
      request
    );

    return response.data;
  }

  @Action
  toggleTruckField(truckFieldChange) {
    this.setTruckFieldStatus(truckFieldChange);
  }

  @Action
  updateTruckFields(truckFields) {
    this.setTruckFields(truckFields);
  }

  @Action
  async loadTruck(truckId: number): Promise<void> {
    this.setCurrentTruck(await this.getTruck(truckId));
  }

  @Action
  async addNewOrigin(model: Object): Promise<Truck> {
    const response = await rawApi.post("/truck-sequencing/add-origin", model);
    return response.data;
  }

  @Action
  async sequenceTruck(model: Object): Promise<Truck> {
    const response = await rawApi.post<Truck>("/truck-sequencing", model);
    this.setCurrentTruck(response.data);
    return response.data;
  }

  @Action
  async getOriginData(originId: any): Promise<any> {
    const response = await rawApi.get(`/truck-sequencing/get-origin/${originId}`);
    return response.data;
  }

  @Action
  async updateOrigin(model: Object): Promise<any> {
    const response = await rawApi.post("/truck-sequencing/update-origin", model);
    return response.data;
  }

  @Action
  async sequenceTruckReverse(model: Object): Promise<Truck> {
    const response = await rawApi.post<Truck>("/truck-sequencing/reverse", model);
    return response.data;
  }

  @Action
  async getStates(): Promise<State[]> {
    const response = await rawApi.get("/system/states");
    return response.data;
  }

  @Action
  async reloadTruck(): Promise<void> {
    if (this.currentTruck) this.setCurrentTruck(await this.getTruck(this.currentTruck.id));
  }

  @Action
  async getTruck(truckId: number): Promise<Truck> {
    const response = await rawApi.get<Truck>(`/trucks/${truckId}`);

    return response.data;
  }

  @Action
  async getTruckCommoditySummary(truckId: number): Promise<any> {
    const response = await rawApi.get(`/trucks/${truckId}/commodity-summary`);
    return response.data;
  }

  @Action
  async getTruckCommodities(truckId: number): Promise<TruckCommodityResource[]> {
    const response = await rawApi.get<TruckCommodityResource[]>(`/trucks/${truckId}/commodities`);
    return response.data;
  }

  @Action
  async createTruck(request: CreateTruckRequest): Promise<CreateTruckResponse> {
    const response = await rawApi.post<CreateTruckResponse>(`/trucks/`, request);

    return response.data;
  }

  @Action
  async getTruckEvents(truckId: number): Promise<TruckEventResource[]> {
    const response = await rawApi.get<TruckEventResource[]>(`/trucks/${truckId}/events`);

    return response.data;
  }

  @Action
  async getTruckOrderFreightDetails(
    request: GetTruckOrderFreightDetailsRequest
  ): Promise<TruckOrderFreightDetailResource[]> {
    const response = await rawApi.get<TruckOrderFreightDetailResource[]>(
      `/trucks/${request.truckId}/orders/${request.orderId}/freight`
    );

    return response.data;
  }

  @Action
  showTruckOrderViewModal(payload) {
    return this.context.dispatch(
      "system/showModal",
      {
        name: modals.truckOrderViewModal,
        data: {selectedItems: payload.orders, truck: payload.truck}
      },
      {root: true}
    );
  }

  @Action
  viewTruckAuditShortcutAction(payload) {
    if (!payload || !payload.selectedItems) {
      return;
    }

    return this.context.dispatch(
      "system/showModal",
      {name: modals.truckAuditLogModal, data: {selectedItems: payload.selectedItems}},
      {root: true}
    );
  }

  @Action
  deleteTruckShortcutAction(payload) {
    if (!payload || !payload.selectedItems) {
      return;
    }

    return this.context.dispatch(
      "system/showModal",
      {name: modals.deleteTruckModal, data: {selectedItems: payload.selectedItems}},
      {root: true}
    );
  }

  @Action
  finalizeTruckShortcutAction(payload) {
    if (!payload || !payload.selectedItems) {
      return;
    }

    return this.context.dispatch(
      "system/showModal",
      {name: modals.finalizeTruckModal, data: {selectedItems: payload.selectedItems}},
      {root: true}
    );
  }

  @Action
  unfinalizeTruckShortcutAction(payload) {
    if (!payload || !payload.selectedItems) {
      return;
    }

    return this.context.dispatch(
      "system/showModal",
      {name: modals.unfinalizeTruckModal, data: {selectedItems: payload.selectedItems}},
      {root: true}
    );
  }

  @Action
  async exportManifestShortcutAction(payload) {
    if (!payload || !payload.selectedItems) {
      return;
    }

    this.setLoading(true);

    try {
      for (let index = 0; index < payload.selectedItems.length; index++) {
        const item = payload.selectedItems[index];

        const response = await rawApi.get(`/trucks/${item.id}/export`, {responseType: "blob"});

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `${item.name}-export.csv`);
        document.body.appendChild(link);
        link.click();
      }
    } catch (ex) {
      throw ex;
    } finally {
      this.setLoading(false);
    }
  }

  @Action
  async printManifestShortcutAction(payload) {
    if (!payload || !payload.selectedItems) {
      return;
    }

    this.setLoading(true);

    try {
      for (let index = 0; index < payload.selectedItems.length; index++) {
        const item = payload.selectedItems[index];

        const response = await rawApi.get(`/trucks/${item.id}/print`, {responseType: "blob"});

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `${item.name}-manifest.pdf`);
        document.body.appendChild(link);
        link.click();
      }
    } catch (ex) {
      notifications.error("Print Manifest", "Failed to load manifest report for truck", ex);
    } finally {
      this.setLoading(false);
    }
  }

  @Action
  async downloadDriverManifestShortcutAction(payload) {
    if (!payload || !payload.selectedItems) {
      return;
    }

    if (payload.selectedItems.length > 0) {
      const item = payload.selectedItems[0];
      let request = {truckId: item.id};
      this.downloadDriverManifest(request);
    }
  }

  @Action
  async downloadDriverManifest(request: DriverManifestRequest) {
    let requestUrl = `/trucks/driverManifest?`;
    if (request.truckId) requestUrl = `/trucks/${request.truckId}/driverManifest`;
    else {
      if (request.deliveryDate)
        requestUrl =
          requestUrl + "deliveryDate=" + formatDate(request.deliveryDate, "MM-DD-YYYY") + "&";
      if (request.facilityCode)
        requestUrl = requestUrl + "facilityCode=" + request.facilityCode + "&";
    }

    this.setLoading(true);

    try {
      const response = await rawApi.get(requestUrl, {responseType: "blob"});
      const contentType = response.headers["Content-Type"];

      const url = window.URL.createObjectURL(new Blob([response.data], {type: contentType}));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", getFileName(response));
      document.body.appendChild(link);
      link.click();
    } catch (ex) {
      throw ex;
    } finally {
      this.setLoading(false);
    }
  }

  @Action
  async mapViewTruckShortcutAction(payload) {
    if (!payload || !payload.selectedItems) {
      return;
    }
    return this.context.dispatch(
      "system/showModal",
      {name: modals.truckMapViewModal, data: {selectedItems: payload.selectedItems}},
      {root: true}
    );
  }

  @Action
  async driverManifestShortcutAction() {
    return this.context.dispatch(
      "system/showModal",
      {name: modals.driverManifestModal, data: {}},
      {root: true}
    );
  }

  @Action
  async generateImportTruckPreview(
    request: GenerateImportTruckPreviewRequest
  ): Promise<GenerateImportTruckPreviewResponse> {
    const formData = new FormData();

    formData.append("file", request.file);
    const response = await rawApi.post<GenerateImportTruckPreviewResponse>(
      `/trucks/importTrucks?format=${request.format}&facilityCode=${request.facilityCode}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      }
    );

    return response.data;
  }

  @Action
  async saveTruckImport(
    request: GenerateImportTruckPreviewResponse
  ): Promise<SaveTruckImportResponse> {
    const response = await rawApi.post<SaveTruckImportResponse>(
      `/trucks/saveImportTrucks`,
      request
    );

    return response.data;
  }

  @Action
  showAddTruckModal() {
    return this.context.dispatch(
      "system/showModal",
      {name: modals.addTruckModal},
      {root: true}
    );
  }

  @Action
  async getAvailableTrucksForFacilityAndDate(payload): Promise<Array<Truck>> {
    let result = await rawApi.get<Array<Truck>>(
      `/trucks?facilities=${payload.facility}&deliveryDate=${payload.deliveryDate}`
    );

    return result.data;
  }

  @Action
  async getCombineTruckSummary(masterTruckId: number): Promise<CombineTruckSummaryResource> {
    let response = await rawApi.get<CombineTruckSummaryResource>(
      `/trucks/${masterTruckId}/combine`
    );

    return response.data;
  }

  @Action
  async combineTrucks(request: CombineTrucksRequest): Promise<CombineTrucksResponse> {
    const response = await rawApi.post<CombineTrucksResponse>(
      `/trucks/${request.parentTruckId}/combine`,
      request
    );

    return response.data;
  }

  @Action
  importTrucksShortcutAction() {
    router.push({name: "ImportTrucks"});
  }

  @Action
  commodityUnloadTimeOverridesShortcutAction() {
    router.push({name: "CommodityUnloadTimeOverrides"});
  }

  @Action
  viewTruckShortcutAction(payload) {
    if (!payload || !payload.selectedItems) {
      return;
    }

    router.push({path: "/trucks/" + payload.selectedItems[0].id});
  }

  @Action
  viewTruckAction(truckId) {
    router.push({path: "/trucks/" + truckId});
  }

  @Action
  scheduleOptimizationShortcutAction(payload) {
    if (!payload || !payload.selectedItems) {
      return;
    }

    router.push({path: "/trucks/" + payload.selectedItems[0].id + "/optimize"});
  }

  @Action
  splitTruckShortcutAction(payload) {
    if (!payload || !payload.selectedItems) {
      return;
    }

    router.push({path: "/trucks/" + payload.selectedItems[0].id + "/split"});
  }

  @Action
  combineTrucksShortcutAction(payload) {
    if (!payload || !payload.selectedItems) {
      return;
    }

    router.push({path: "/trucks/" + payload.selectedItems[0].id + "/combine"});
  }

  @Action
  routingTruckShortcutAction(payload) {
    if (!payload || !payload.selectedItems) {
      return;
    }

    router.push({path: "/trucks/" + payload.selectedItems[0].id + "/route"});
  }

  @Action
  async previewAddOrderToTruck(
    payload: PreviewAddOrderToTruckRequest
  ): Promise<PreviewAddOrderToTruckResource[]> {
    let query: string = "";

    payload.orderNumbers.forEach(orderNumber => {
      query += `OrderHeaderNumbers=${orderNumber}&`;
    });
    let response = await rawApi.get<PreviewAddOrderToTruckResource[]>(`/trucks/orders?${query}`);

    return response.data;
  }

  @Action
  addOrderToTruck(payload: AddOrderToTruckRequest) {
    return api.postAsync(`/trucks/${payload.truckId}/orders`, payload);
  }

  @Action
  updateTruckDetailSequence(payload: UpdateTruckDetailSequenceRequest) {
    return api.postAsync(`/trucks/${payload.truckId}/updateSequence`, payload);
  }

  @Action
  async addNonCdsOrderToTruck(
    payload: AddNonCdsOrderToTruckRequest
  ): Promise<AddNonCdsOrderToTruckResponse> {
    const response = await rawApi.post<AddNonCdsOrderToTruckResponse>(
      `/trucks/${payload.truckId}/nonCdsOrders`,
      payload
    );

    return response.data;
  }

  @Action
  async deleteTruck(request: DeleteTruckRequest): Promise<DeleteTruckResponse> {
    const response = await rawApi.delete(`/trucks/${request.truckId}`, {data: request});

    if (request.confirm) this.removeDeletedTruckFromList(request.truckId);

    return response.data;
  }

  @Action
  async finalizeTruck(request: FinalizeTruckRequest): Promise<FinalizeTruckResponse> {
    const response = await rawApi.post(`/trucks/${request.truckId}/finalize`, request);
    return response.data;
  }

  @Action
  async unfinalizeTruck(request: UnfinalizeTruckRequest): Promise<UnfinalizeTruckResponse> {
    const response = await rawApi.post(`/trucks/${request.truckId}/unfinalize`, request);
    return response.data;
  }

  @Action
  async removeOrderFromTruck(request: RemoveOrderFromTruckRequest): Promise<void> {
    const response = await rawApi.delete(`/trucks/${request.truckId}/orders/${request.orderId}`);
  }

  @Action
  async splitTruck(): Promise<SplitTruckResponse> {
    let request: SplitTruckRequest = {
      orderIdsForNewTruck: this.splitTruckSummary.newTruck.orders.map(x => x.orderId),
      existingTruckName: this.splitTruckSummary.existingTruck.truckName,
      newTruckName: this.splitTruckSummary.newTruck.truckName,
      newTruckDeliveryDate: this.splitTruckSummary.newTruck.deliveryDate,
      newTruckMaxCubes: this.splitTruckSummary.newTruck.maxCubes,
      truckId: this.splitTruckSummary.existingTruck.truckId,
      NewTruckZip3sServiced: this.splitTruckSummary.newTruck.zip3sServiced
    };

    const response = await rawApi.post<SplitTruckResponse>(
      `/trucks/${request.truckId}/split`,
      request
    );

    return response.data;
  }

  @Action
  async updateTruck(): Promise<UpdateTruckResponse> {
    let request: UpdateTruckRequest = {
      id: this.currentTruck.id,
      truckName: this.currentTruck.name,
      deliveryDate: this.currentTruck.deliveryDate,
      zip3sServiced: this.currentTruck.zip3sServiced,
      driverId: this.currentTruck.driverId,
      maxCubes: this.currentTruck.maxCubes,
      tripDays: this.currentTruck.tripDays,
      status: this.currentTruck.status
    };

    const response = await rawApi.put<UpdateTruckResponse>(`/trucks/${request.id}`, request);

    return response.data;
  }

  @Action
  async getDrivers(request: GetDriversRequest): Promise<DriverListItemResource[]> {
    let url = "/drivers?";

    if (request.facilityCode) {
      url += `facilityCode=${request.facilityCode}&`;
    }

    if (request.includeInactive) {
      url += `includeInactive=true`;
    }

    const response = await rawApi.get<DriverListItemResource[]>(url);

    return response.data;
  }

  @Action
  async getDriver(id: number): Promise<DriverResource> {
    const response = await rawApi.get(`/drivers/${id}`);

    return response.data;
  }

  @Action
  async createDriver(request: CreateDriverRequest): Promise<CreateDriverResponse> {
    const response = await rawApi.post("/drivers", request);

    return response.data;
  }

  @Action
  async updateDriver(request: UpdateDriverRequest): Promise<UpdateDriverResponse> {
    const response = await rawApi.put(`/drivers/${request.id}`, request);

    return response.data;
  }

  @Action
  async getCarriers(request: GetCarrierRequest): Promise<CarrierResource[]> {
    let url = "/carriers?";

    if (request.facilityCode) {
      url += `facilityCode=${request.facilityCode}`;
    }
    const response = await rawApi.get<CarrierResource[]>(url);
    return response.data;
  }

  @Action
  async getImportTruckFormats(): Promise<ImportTruckFormat[]> {
    const response = await rawApi.get<ImportTruckFormat[]>(`/truckImportFormats`);

    return response.data;
  }

  @Action
  async geoCodeAddressForSplitView(payload: any) {
    const result = await api.postAsync(`/geocode/${payload.companyId}/${payload.orderHeader}`, payload.address);

    const passModel = {
      payload: payload,
      result: result
    }
    this.setSplitViewSummary(passModel);
  }

  @Mutation
  setSplitViewSummary(payload: any) {
    const truckToModify = payload.payload.isOriginalTruck ? this.splitTruckSummary.existingTruck : this.splitTruckSummary.newTruck;
    if (!truckToModify) return;

    const orderToSet = _.find(truckToModify.orders, (order) => {
      return order.orderId === payload.payload.orderId;
    })

    if (!orderToSet) return;
    orderToSet.location = payload.result.data.location;
    orderToSet.stopParty.location = payload.result.data.location;
  }

  @Action
  async geoCodeSplitViewAddress2(payload: any) {
    const result = await api.postAsync(`/geocode/${payload.companyId}/${payload.orderHeader}`, payload.address);
    const passModel = {
      payload: payload,
      result: result
    }
    if (!result.data || !result.data.isSuccess)
      throw new Error("Failed to geocode address.")
    this.setSplitViewTruck2(passModel);
  }

  @Mutation
  setSplitViewTruck2(payload: any) {
    if(!this.splitTruckSummary.newTruck) return;
    const foundOrder =  _.find(this.splitTruckSummary.newTruck.orders, (order) => {
      return order.orderId === payload.payload.orderId;
    })

    if(!foundOrder) return;
    foundOrder.location = payload.result.data.location;
    foundOrder.stopParty.location = payload.result.data.location;
  }

  @Action
  async geoCodeSplitViewAddress1(payload: any) {
    const result = await api.postAsync(`/geocode/${payload.companyId}/${payload.orderHeader}`, payload.address);
    const passModel = {
      payload: payload,
      result: result
    }
    if (!result.data || !result.data.isSuccess)
      throw new Error("Failed to geocode address.")
    this.setSplitViewTruck1(passModel);
  }

  @Mutation
  setSplitViewTruck1(payload: any) {
    if(!this.splitTruckSummary.existingTruck) return;
    const foundOrder =  _.find(this.splitTruckSummary.existingTruck.orders, (order) => {
      return order.orderId === payload.payload.orderId;
    })

    if(!foundOrder) return;
    foundOrder.location = payload.result.data.location;
    foundOrder.stopParty.location = payload.result.data.location;
  }

  @Action
  async geoCodeAddress(payload: any) {
    const result = await api.postAsync(`/geocode/${payload.companyId}/${payload.orderHeader}`, payload.address);
    const passModel = {
      payload: payload,
      result: result
    } 
    if (!result.data || !result.data.isSuccess)
      throw new Error("Failed to geocode address.")
    this.setTruckOrderLocationFromGeocode(passModel);
  }

  @Action
  async updateAddressCoordinatesSplitView2(payload: any) {
    const passModel = {
      latitude: parseFloat(payload.latitude),
      longitude: parseFloat(payload.longitude),
      id: payload.id,
      orderHeader: payload.orderHeader,
      orderId: payload.orderId
    }

    const result = await api.postAsync(`/geocode/update-company`, passModel);
    if (!result.data || !result.data.isSuccess)
      throw new Error("Failed to set coordinates.")
    this.setTruckOrderSplitSummaryNewTruck(passModel);
  }

  @Mutation
  setTruckOrderSplitSummaryNewTruck(model: any) {
    if(!this.splitTruckSummary) return;
    const foundOrder =  _.find(this.splitTruckSummary.newTruck.orders, (order) => {
      return order.orderId === model.orderId;
    })

    if(!foundOrder) return;
    let location = {isZipCodeLocation: false, latitude: model.latitude, longitude: model.longitude};

    if(foundOrder.stopParty)
    {
      foundOrder.stopParty.location = location;
    }
    foundOrder.location = location;
  }

  @Action
  async updateAddressCoordinatesSplitView1(payload: any) {
    const passModel = {
      latitude: parseFloat(payload.latitude),
      longitude: parseFloat(payload.longitude),
      id: payload.id,
      orderHeader: payload.orderHeader,
      orderId: payload.orderId
    }

    const result = await api.postAsync(`/geocode/update-company`, passModel);
    if (!result.data || !result.data.isSuccess)
      throw new Error("Failed to set coordinates.")
    this.setTruckOrderSplitSummaryExistingTruck(passModel);
  }

  @Mutation
  setTruckOrderSplitSummaryExistingTruck(model: any) {
    if(!this.splitTruckSummary) return;
    const foundOrder =  _.find(this.splitTruckSummary.existingTruck.orders, (order) => {
      return order.orderId === model.orderId;
    })

    if(!foundOrder) return;
    let location = {isZipCodeLocation: false, latitude: model.latitude, longitude: model.longitude};

    if(foundOrder.stopParty)
    {
      foundOrder.stopParty.location = location;
    }
    foundOrder.location = location;
    console.log(foundOrder);
  }

  @Action
  async updateAddressCoordinates(model: any) {
    const passModel = {
      latitude: parseFloat(model.latitude),
      longitude: parseFloat(model.longitude),
      id: model.id,
      orderHeader: model.orderHeader
    }

    const result = await api.postAsync(`/geocode/update-company`, passModel);
    if (!result.data || !result.data.isSuccess)
      throw new Error("Failed to set coordinates.")
    this.setTruckOrderLocationFromUpdateCoordinates(model);
  }

  @Mutation
  setTruckOrderLocationFromUpdateCoordinates(model: any) {
    if(!this.currentTruck) return;
    const foundOrder =  _.find(this.currentTruck.orders, (order) => {
      return order.id === model.orderId;
    })

    if(!foundOrder) return;
    let location = {isZipCodeLocation: false, latitude: model.latitude, longitude: model.longitude};

    if(foundOrder.stopParty)
    {
      foundOrder.stopParty.location = location;
    }
    foundOrder.location = location;
  }

  @Mutation
  setTruckOrderLocationFromGeocode(model: any) {
    if (!this.currentTruck) return;
    const foundOrder = _.find(this.currentTruck.orders, (order) => {
      return order.id === model.payload.orderId;
    })

    if(!foundOrder) return;
    foundOrder.location = model.result.data.location;
    foundOrder.stopParty.location = model.result.data.location;
  }

  @Mutation
  setFilters(filters: TruckListFilters) {
    this.filters = JSON.parse(JSON.stringify(filters));
  }

  @Mutation
  resetFilters() {
    this.filters = defaultFilters();
  }

  @Mutation
  setCurrentTruck(truck: Truck) {
    this.currentTruck = truck;
  }

  @Mutation
  setTruckFieldStatus(truckFieldChange) {
    this.truckFields[truckFieldChange.idx].hidden = truckFieldChange.value;
  }

  @Mutation
  setSplitTruckSummary(summary: SplitTruckSummary) {
    this.splitTruckSummary = summary;
  }

  @Mutation
  setTruckFields(fields) {
    this.truckFields = fields;
  }

  @Mutation
  setTruckList(response: AxiosResponse<TruckListItem[]>) {
    this.pageData.totalItems = response.headers.totalitems;
    this.pageData.totalPages = response.headers.totalpages;
    if (this.data) {
      this.data = this.data.concat(response.data);
    } else {
      this.data = response.data;
    }
  }

  @Mutation
  resetTruckList() {
    this.pageData.totalItems = 0;
    this.pageData.totalPages = 0;
    this.data = [];
  }

  @Mutation
  setLoading(isLoading: boolean) {
    this.loading = isLoading;
  }

  @Mutation
  setCurrentPage(number: number) {
    this.pageData.currentPage = number;
  }

  @Mutation
  switchTruckSelectionStatus(truckId: number) {
    var index = this.data.findIndex(function (item) {
      return item.id === truckId;
    });

    if (index < 0) {
      return;
    }

    const existing = this.data[index];

    existing.selected = !existing.selected;

    this.data = [...this.data.slice(0, index), existing, ...this.data.slice(index + 1)];
  }

  @Mutation
  removeDeletedTruckFromList(truckId: number) {
    let index = this.data.findIndex(x => x.id === truckId);
    this.data.splice(index, 1);
  }

  @Mutation
  setSplitTruckSummaryOriginalOrders(orders: SplitTruckSummaryOrder[]) {
    this.splitTruckSummary.existingTruck.orders = JSON.parse(JSON.stringify(orders));
  }

  @Mutation
  setSplitTruckSummaryNewOrders(orders: SplitTruckSummaryOrder[]) {
    this.splitTruckSummary.newTruck.orders = JSON.parse(JSON.stringify(orders));
  }

  @Mutation
  setSplitTruckSummaryDeliveryDate(payload: UpdateSplitTruckSummaryField) {
    if (payload.original) this.splitTruckSummary.existingTruck.deliveryDate = payload.value;
    else this.splitTruckSummary.newTruck.deliveryDate = payload.value;
  }

  @Mutation
  setSplitTruckSummaryTruckName(payload: UpdateSplitTruckSummaryField) {
    if (payload.original) this.splitTruckSummary.existingTruck.truckName = payload.value;
    else this.splitTruckSummary.newTruck.truckName = payload.value;
  }

  @Mutation
  setSplitTruckSummaryMaxCubes(payload: UpdateSplitTruckSummaryField) {
    if (payload.original) this.splitTruckSummary.existingTruck.maxCubes = payload.value;
    else this.splitTruckSummary.newTruck.maxCubes = payload.value;
  }

  @Mutation
  setSplitTruckSummaryZips(payload: UpdateSplitTruckSummaryField) {
    if (payload.original)
      this.splitTruckSummary.existingTruck.zip3sServiced = JSON.parse(
        JSON.stringify(payload.value)
      );
    else this.splitTruckSummary.newTruck.zip3sServiced = JSON.parse(JSON.stringify(payload.value));
  }

  @Mutation
  setTruckName(name: string) {
    this.currentTruck.name = name;
  }

  @Mutation
  setDeliveryDate(val: Date) {
    this.currentTruck.deliveryDate = val;
  }

  @Mutation
  setTruckStatus(val: string) {
    this.currentTruck.status = val;
  }

  @Mutation
  setDriverId(val: number) {
    this.currentTruck.driverId = val;
  }

  @Mutation
  setTripDays(val: number) {
    this.currentTruck.tripDays = val;
  }

  @Mutation
  setMaxCubes(val: number) {
    this.currentTruck.maxCubes = val;
  }

  @Mutation
  setZips(val: string[]) {
    this.currentTruck.zip3sServiced = JSON.parse(JSON.stringify(val));
  }

  get getSelectedTrucks(): Array<TruckListItem> {
    if (!this.data) return [];

    return this.data.filter(x => x.selected);
  }

  get getSelectedTruckCount(): number {
    return this.getSelectedTrucks.length;
  }

  @Mutation
  setTruckOrder(orders: any[]) {
    _.forEach(orders, order => {
      let orderToChange = _.find(this.currentTruck.orders, (o) => {
        return o.orderHeaderNumber === order.orderHeaderNumber;
      });

      if(orderToChange) {
        orderToChange.sequence = order.sequence;
      }
    })

    this.currentTruck.orders = _.orderBy(this.currentTruck.orders, ['sequence']);
  }
}
